import type { EventSnapshot, PresenterExport } from '../types';
import { parseEventSnapshot } from './api';

interface PresenterResponse {
  snapshot: EventSnapshot;
}

function endpoint(): string {
  const base = import.meta.env.VITE_SUPABASE_URL?.replace(/\/+$/, '');
  if (!base) throw new Error('Supabase is not configured.');
  return `${base}/functions/v1/presenter`;
}

export function parsePresenterResponse(value: unknown): PresenterResponse {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('snapshot' in value)
  ) {
    throw new Error('Presenter response was invalid.');
  }
  return { snapshot: parseEventSnapshot(value.snapshot) };
}

export function parsePresenterExport(value: unknown): PresenterExport {
  if (
    typeof value !== 'object' || value === null ||
    !('snapshot' in value) || !('comments' in value) || !Array.isArray(value.comments) ||
    !('exportedAt' in value) || typeof value.exportedAt !== 'string' ||
    !Number.isFinite(Date.parse(value.exportedAt))
  ) {
    throw new Error('Presenter export was invalid.');
  }
  const snapshot = parseEventSnapshot(value.snapshot);
  const topicIds = new Set(snapshot.topics.map(topic => topic.id));
  if (topicIds.size !== snapshot.topics.length || snapshot.topics.some(topic =>
    !topic.id || [topic.likeCount, topic.flowerCount, topic.commentCount]
      .some(count => !Number.isSafeInteger(count) || count < 0)
  )) {
    throw new Error('Presenter export topic totals were invalid.');
  }
  const commentIds = new Set<string>();
  const commentCounts = new Map<string, number>();
  const comments = value.comments.map((comment: unknown) => {
    if (
      typeof comment !== 'object' || comment === null ||
      !('id' in comment) || typeof comment.id !== 'string' || !comment.id ||
      commentIds.has(comment.id) ||
      !('topicId' in comment) || typeof comment.topicId !== 'string' ||
      !topicIds.has(comment.topicId) ||
      !('nickname' in comment) || typeof comment.nickname !== 'string' ||
      !('body' in comment) || typeof comment.body !== 'string' ||
      !('createdAt' in comment) || typeof comment.createdAt !== 'string' ||
      !Number.isFinite(Date.parse(comment.createdAt)) ||
      !('isHidden' in comment) || typeof comment.isHidden !== 'boolean'
    ) {
      throw new Error('Presenter export comment was invalid.');
    }
    commentIds.add(comment.id);
    commentCounts.set(comment.topicId, (commentCounts.get(comment.topicId) ?? 0) + 1);
    return {
      id: comment.id, topicId: comment.topicId, nickname: comment.nickname,
      body: comment.body, createdAt: comment.createdAt, isHidden: comment.isHidden,
    };
  });
  if (snapshot.topics.some(topic => topic.commentCount !== (commentCounts.get(topic.id) ?? 0))) {
    throw new Error('Presenter export comment totals do not match. Please retry.');
  }
  return { snapshot, comments, exportedAt: value.exportedAt };
}

async function request(body: Record<string, unknown>): Promise<unknown> {
  const response = await fetch(endpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      typeof payload === 'object' &&
      payload !== null &&
      'error' in payload &&
      typeof payload.error === 'string'
        ? payload.error
        : 'Presenter request failed.';
    throw new Error(message);
  }
  return payload;
}

export const presenterApi = {
  reset(): Promise<PresenterResponse> {
    return request({ action: 'reset', confirmation: 'RESET MEETING' }).then(parsePresenterResponse);
  },
  export(): Promise<PresenterExport> {
    return request({ action: 'export' }).then(parsePresenterExport);
  },
  status(): Promise<PresenterResponse> {
    return request({ action: 'status' }).then(parsePresenterResponse);
  },
  update(changes: {
    currentTopicId?: string | null;
    audienceFollowEnabled?: boolean;
    audienceBrowsingEnabled?: boolean;
  }): Promise<PresenterResponse> {
    return request({ action: 'update', ...changes }).then(parsePresenterResponse);
  },
};
